// "PUSISTE 5 PERSONAS EN EL HOGAR, PERO SOLO HAY 3 LLENAS."
//
// Lissette, 2026-09-15: *"cuando se edite un cliente y se coloque el total de
// miembros y no está lleno como tal, y se vaya a guardar, dé un anuncio de que
// falta miembro y dé opción de continuar así o seguir editando"*.
//
// `householdSize` es un desplegable: la agente elige 1…10. Las personas de
// verdad son otra cosa — el titular, la pareja si la hay, y los dependientes
// que tengan nombre. Nada obliga a que cuadren, y cuando no cuadran no lo dice
// nadie: el número se queda escrito y **de él dependen el subsidio y la
// elegibilidad** que se calculan con el tamaño del hogar.
//
// ── QUÉ CUENTA COMO "LLENO", Y POR QUÉ ────────────────────────────────────
//
// - El TITULAR siempre cuenta: la ficha es suya.
// - La PAREJA cuenta con la misma regla que el resto del CRM
//   (`hasCurrentSpouse`: estado civil con pareja **y** nombre escrito). Un
//   apartado de cónyuge en blanco no es una persona.
// - Un DEPENDIENTE cuenta si tiene nombre o apellido. Una fila vacía que se
//   añadió sin querer no es nadie.
// - **Y el dependiente con ficha propia (`separateProfile`) SÍ cuenta**, al
//   revés que en `peopleFromClient`/`householdIncomeFromClient`. Allí se
//   excluye a propósito, porque el Marketplace los declara en su propia
//   solicitud. Aquí la pregunta es otra: "¿está esta persona escrita?", y sí lo
//   está. Contarla fuera haría saltar el aviso en toda familia con un hijo
//   mayor de edad separado — un aviso que salta cuando no toca se aprende a
//   ignorar, y entonces deja de servir el día que sí toca.
//
// ── Y SOLO AVISA EN UN SENTIDO ────────────────────────────────────────────
//
// Solo cuando el número declarado es MAYOR que las personas escritas, que es lo
// que ella describió ("falta miembro"). Si es menor, el hogar tiene más gente
// de la declarada: es otro descuadre, con otro mensaje, y no se inventa aquí
// sin pedírselo.
//
// Módulo PLANO —sin React— para poder probarlo desde vitest (regla de oro 48).
import { hasCurrentSpouse } from './policyTypes.js';

const conTexto = (v) => String(v ?? '').trim() !== '';

/** Cuántas personas hay REALMENTE escritas en la ficha. Nunca menos de 1. */
export function personasEnLaFicha(ficha) {
  if (!ficha) return 0;
  let n = 1; // el titular
  if (hasCurrentSpouse(ficha)) n += 1;
  for (const d of (Array.isArray(ficha.dependents) ? ficha.dependents : [])) {
    if (d && (conTexto(d.firstName) || conTexto(d.lastName))) n += 1;
  }
  return n;
}

/**
 * ¿Falta gente por llenar? Devuelve `{ declarado, escritos, faltan }` o `null`
 * cuando no hay nada que avisar.
 *
 * Sin número declarado NO se avisa: "no lo he puesto" no es un descuadre, y
 * regañar por un campo que la agente decidió dejar vacío es ruido.
 */
export function faltanMiembrosDelHogar(ficha) {
  const crudo = String(ficha?.householdSize ?? '').trim();
  if (!crudo) return null;
  const declarado = Number(crudo);
  if (!Number.isInteger(declarado) || declarado <= 0) return null;
  const escritos = personasEnLaFicha(ficha);
  if (declarado <= escritos) return null;
  return { declarado, escritos, faltan: declarado - escritos };
}

/** El aviso, en los dos idiomas. Vive aquí para poder probar el texto. */
export function avisoDeMiembrosQueFaltan(falta, es = true) {
  if (!falta) return null;
  const { declarado, escritos, faltan } = falta;
  const persona = (n) => (es ? (n === 1 ? 'persona' : 'personas') : (n === 1 ? 'person' : 'people'));
  return {
    title: es ? 'Falta gente del hogar por llenar' : 'Some household members are missing',
    message: es
      ? `Pusiste ${declarado} ${persona(declarado)} en el hogar, pero solo ${escritos === 1 ? 'hay 1 llena' : `hay ${escritos} llenas`}: ${faltan === 1 ? 'falta 1' : `faltan ${faltan}`}.\n\nEl tamaño del hogar es con lo que se calculan el subsidio y la elegibilidad, así que conviene que cuadre. Puedes guardar así y completarlo después.`
      : `You set ${declarado} ${persona(declarado)} in the household, but only ${escritos} ${escritos === 1 ? 'is' : 'are'} filled in: ${faltan} missing.\n\nHousehold size is what the subsidy and eligibility are calculated from, so it's worth matching. You can save as is and finish it later.`,
    confirmLabel: es ? 'Guardar así' : 'Save as is',
    cancelLabel: es ? 'Seguir editando' : 'Keep editing',
  };
}
